import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
// components
import { ExcelExport } from '@progress/kendo-react-excel-export';
import CustomExcelExport from '../CustomExcelExport';
import BaseGrid from '../BaseGrid';
import GridLoader from '../GridLoader';
import GridErrorMessage from '../GridErrorMessage';
import GridFiltersBar from '../GridFiltersBar';
import { RestoreCell, CheckboxCell } from '../Cells';
import { Badge, Modal, requestStatuses } from '@gulfwinds/ui-components';
import Input from 'shared/inputs/BaseInput';
import Button, {
  AddButton, ExportButton, RecycleBinButton, FilterButton,
} from 'shared/buttons';
import ColumnsToDisplayModal from '../old/BaseGrid/components/modals/ColumnsToDisplayModal';
import FilteringPanel from '../../filter';
// hooks
import useGridData from '../hooks/useGridData';
import useSortPageState from '../hooks/useSortPageState';
import useDataExport from '../hooks/useDataExport';
import exportDataHandlingStrategies from '../hooks/exportDataHandlingStrategies';
import useSearchParams from 'shared/../hooks/useSearchParams';
import useSelect from '../hooks/useSelect';
// consts
import { initialFilter } from '../../filter/constants';
// Fetch
import { getFilteredData } from 'src/fetch/filtering';
// Styles
import './style.scss';
import { theme } from 'src/theme';
import { useDataGridSettingsCache } from '../../../hooks/useDataGridSettingsCache';
import { BaseHotKeys } from 'shared/inputs/BaseHotKeys';
import { useToast } from 'shared/toast';
import { getDateStringForRequest } from 'src/shared/helpers/date';

if (theme.mode === 'dark') {
  import(/* webpackChunkName: "dark" */ './style.scss?dark');
}

const defaultPage = { skip: 0, take: 25 };

const defaultSortable = { allowUnsort: true, mode: 'single' };

const defaultSelected = [];
const defaultRemapFields = (item) => item;
/**
 * GridContent component.
 *
 * @typedef {object} GridContentProps
 * @property {string} id - ID of the grid - used for generating data-testid.
 * @property {string} name - Name of the grid content.
 * @property {string} title - Title for the grid.
 * @property {boolean} isLoading - Indicates if data is loading (default: false).
 * @property {React.ComponentType} gridComponent - Custom grid component (default: BaseGrid).
 * @property {Array} columns - Configuration for grid columns.
 * @property {boolean} groupable - Enable grouping (default: false).
 * @property {object} group - Grouping configuration.
 * @property {object | boolean | null} sortable - Telerik sortable settings
 * @property {boolean} isMultiSorting - Whether to use multi sorting.
 * @property {object} initialSort - Initial sorting configuration.
 * @property {object} initialPage - Initial pagination configuration.
 * @property {string} identifierColumn - Column name for unique identifiers (default: 'id').
 * @property {function} onAdd - Callback for adding items.
 * @property {Array} data - Data for the grid.
 * @property {function} getData - Function to retrieve data.
 * @property {boolean} reloadOnSort - Indicates if data should reload on sorting (default: true).
 * @property {boolean} isLocalData - Indicates if data is local (default: false).
 * @property {boolean} forceReload - Force data reload (default: false).
 * @property {boolean} forceBgReload - Force background data reload (default: false).
 * @property {boolean} didLoad - Indicates if data has loaded.
 * @property {boolean} hasExport - Enable export functionality (default: true).
 * @property {function} requestExport - Callback for requesting data export.
 * @property {object} exportOptions - Export options configuration.
 * @property {Array} exportColumns - Columns to include in export.
 * @property {boolean} hasRecycleBin - Enable recycle bin (default: false).
 * @property {boolean} initialRecycleBin - Initial recycle bin state (default: false).
 * @property {function} onShowRecycleBin - Callback for showing the recycle bin.
 * @property {function} onHideRecycleBin - Callback for hiding the recycle bin.
 * @property {function} onRestore - Callback for restoring items from recycle bin.
 * @property {boolean} allowItemRestore - Indicates if item restoration is allowed.
 * @property {boolean} hideItemRestore - Indicates if item restoration is hidden.
 * @property {function} onRowSelect - Callback when a row is selected.
 * @property {boolean} selectionEnabled - Enable row selection (default: false).
 * @property {boolean} hideHeaderSelect - Hide header select checkbox (default: false).
 * @property {boolean} onlyShowSelected - Show only selected rows (default: false).
 * @property {Array} selected - Selected row identifiers.
 * @property {function} onSelectionChange - Callback for selection changes.
 * @property {ReactNode} actionBarContent - Content for the action bar.
 * @property {boolean} hideActionBar - Hide the action bar (default: false).
 * @property {boolean} hideActionsCell - Hide actions cell (default: false).
 * @property {string} searchValue - Search value.
 * @property {object} recycleBin - Recycle bin configuration.
 * @property {boolean} showRecycleBin - Show the recycle bin.
 * @property {boolean} hideRecycleBin - Hide the recycle bin.
 * @property {object} searchParams - Search parameters.
 * @property {object} filteringOptions - Filtering options.
 * @property {function} enableColumnsToDisplay - Enable columns to display.
 * @property {Array} columnsToDisplay - Columns to display.
 * @property {boolean} enableBulkChange - Enable bulk change.
 * @property {boolean} bulkChangeActive - Indicates if bulk change is active.
 * @property {function} setBulkChangeActive - Callback for setting bulk change state.
 * @property {boolean} disableAdd - Disable adding items (default: false).
 * @property {Array} additionalButtons - Additional buttons.
 * @property {number} resultCount - Total result count.
 * @property {boolean} showResultCount - Show result count.
 * @property {function} remapFields - Function to remap item fields.
 * @property {boolean} usePageAdd - When used will use an add button that routes to new.
 * @property {...import('../BaseGrid/BaseGrid').BaseGridProps} props - Additional props.
 *
 * @param {GridContentProps} props - Component props.
 * @returns {React.ReactElement} - GridContent component.
 */

const GridContent = ({
  id,
  name,
  title,
  isLoading = false,
  // grid
  gridComponent = BaseGrid,
  columns,
  groupable = false,
  group,
  sortable = defaultSortable,
  isMultiSorting = false,
  initialSort,
  initialPage = defaultPage,
  // data
  identifierColumn = 'id',
  onAdd,
  data: passedData,
  getData,
  reloadOnSort = true,
  isLocalData = false,
  // force reload
  forceReload = false,
  forceBgReload = false,
  didLoad,
  // export
  hasExport = true,
  requestExport,
  exportOptions = {
    dataHandlingStrategy: exportDataHandlingStrategies.GENERATE_SPREADSHEET_LOCALLY,
  },
  exportColumns,
  // recycle bin
  hasRecycleBin = false,
  initialRecycleBin = false,
  onShowRecycleBin,
  onHideRecycleBin,
  onRestore,
  allowItemRestore,
  hideItemRestore = false,
  // selection
  onRowSelect,
  selectionEnabled = false,
  hideHeaderSelect = false,
  onlyShowSelected = false,
  selected = defaultSelected,
  onSelectionChange,
  onPageChange: customPageChangeHandler,
  // custom
  actionBarContent,
  hideActionBar = false,
  hideActionsCell = false,
  // search
  searchValue,
  recycleBin,
  showRecycleBin,
  hideRecycleBin,
  searchParams,
  filteringOptions,
  enableColumnsToDisplay,
  columnsToDisplay,
  enableBulkChange,
  bulkChangeActive,
  setBulkChangeActive,
  disableAdd = false,
  additionalButtons,
  resultCount,
  showResultCount,
  remapFields = defaultRemapFields,
  usePageAdd = true,
  onFilterChange,
  showRestoreColumn = true,
  ...props
}) => {
  const { readOnly, forceAdd } = props;
  const GridComponent = gridComponent;
  const toast = useToast();

  const filterRef = useRef(null);
  const [currentFilter, setCurrentFilter] = useState(initialFilter);
  const [columnsToDisplayOpened, setColumnsToDisplayOpened] = useState(false);

  const getDataWithFilter = (params) => {
    if (filterRef.current && filteringOptions) {
      return getFilteredData(filteringOptions.entity, filterRef.current, filteringOptions.paginated, params);
    }

    if (getData) {
      const parameters = { ...params };
      const pageSize = props.pageSize || params.pageSize;
      if (pageSize) { parameters.pageSize = pageSize; }
      return getData(parameters);
    }

    return Promise.resolve({ data: passedData, length: passedData.length });
  };

  // Pagination and sorting
  const {
    innerSortable, sort, onSortChange, resetSort, clearSortCache, page, onPageChange, sortParams, pageParams,
  } = useSortPageState({
    initialSort, sortable, isMultiSorting, initialPage, columns, gridName: name, enableSortCache: props?.enableSortCache,
  });

  const onFetchError = () => clearSortCache();

  // Grid data loading
  const {
    status,
    items,
    total,
    subTotal,
    error,
    reload,
    reloadOnBackground,
    isBgLoading,
  } = useGridData({
    getData: getDataWithFilter,
    searchParams,
    reloadOnSort,
    groupable,
    isLocalData,
    sort,
    page,
    onPageChange,
    onFetchError,
    sortParams,
    pageParams,
    onlyShowSelected,
    selected,
    remapFields,
  });

  useEffect(() => {
    onFilterChange?.(currentFilter);
  }, [currentFilter]);

  // force reload
  useEffect(() => {
    if (forceReload) {
      reload();
    }
  }, [forceReload]);

  useEffect(() => {
    if (forceBgReload) {
      reloadOnBackground();
    }
  }, [forceBgReload]);

  // on loaded
  useEffect(() => {
    if (didLoad && !isBgLoading && status === requestStatuses.SUCCESS) {
      didLoad(items, searchParams);
    }
  }, [status, isBgLoading]);

  useEffect(() => {
    if (!filteringOptions) {
      return;
    }

    const savedFilter = window.sessionStorage.getItem(`${filteringOptions.entity}.filter`);

    if (savedFilter) {
      handleFilterApplied({
        filter: JSON.parse(savedFilter),
      });
      window.sessionStorage.removeItem(`${filteringOptions.entity}.filter`);
    }
  }, []);

  // recycle bin
  const [restored, setRestored] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);

  const onRestoreHandler = (dataItem) => () => onRestore(
    dataItem,
    () => setRestored((prev) => [...prev, dataItem[identifierColumn]]), // onSuccess
  );

  // export
  const _export = useRef(null);
  const _grid = useRef();

  const {
    status: exportStatus,
    setStatus: setExportStatus,
    exported,
    exporterRef,
    exportData,
  } = useDataExport(name, requestExport, searchParams, exportOptions);

  const defaultExport = async () => {
    if (_export.current !== null) {
      if (items.length >= total) {
        _export.current.save(passedData ?? items, _grid.current.columns);
      } else {
        // if all there, don't go to db
        setExportStatus(requestStatuses.PENDING);
        try {
          // in case of adds overshoot page size
          const allData = await getDataWithFilter({ ...sortParams, ...searchParams, pageSize: total + 10, page: 1 });
          const mappedData = { ...allData, data: allData.data.map(remapFields) };
          _export.current.save(mappedData, _grid.current.columns);
          setExportStatus(requestStatuses.SUCCESS);
        } catch (e) {
          console.error(`Error loading ${name} for Export`, error);
          toast.error({ message: `Failed to export ${name}`, error });
          setExportStatus(requestStatuses.FAILED);
        }
      }
    }
  };

  const onExport = requestExport ? exportData : defaultExport;

  const fileName = `${name ?? props.itemName}_${getDateStringForRequest(new Date(), true)}`;

  // Select mode
  const {
    allSelected, onItemSelectHandler, onRowSelectHandler, onHeaderSelectionChange,
  } = useSelect({
    selectionEnabled,
    selected,
    onSelectionChange,
    onRowSelect,
    identifierColumn,
    itemsInPage: items,
  });

  const handleFilterApplied = ({ filter }) => {
    const { hidden } = filter;
    filterRef.current = filter.expression;

    if (!hidden) {
      setCurrentFilter(filter);
    }

    reload();

    if (!hidden) {
      setShowFilter(false);
      setFilterApplied(true);
    }
  };

  const handleClearFilter = () => {
    filterRef.current = null;
    setFilterApplied(false);
    setCurrentFilter(initialFilter);
    reload();
  };

  const handleBulkChangeClick = (status) => {
    setBulkChangeActive(status);
  };

  // grid columns
  const buildColumns = () => {
    let result = [...(columns ?? [])];
    if (recycleBin && !hideItemRestore && showRestoreColumn) {
      result = [
        ...result,
        {
          title: 'Restore',
          isSystem: true,
          sortable: false,
          cell: ({ dataItem, ...props }) => (
            <RestoreCell
              {...props}
              onRestore={onRestoreHandler(dataItem)}
              restored={
                restored.includes(dataItem[identifierColumn]) || (allowItemRestore && !allowItemRestore(dataItem))
              }
            />
          ),
        },
      ];
    }
    if (selectionEnabled) {
      result = [
        {
          title: null, // if it's just an empty string, the field name is shown in the title
          field: 'checked',
          width: 60,
          headerSelectionValue: allSelected,
          ...hideHeaderSelect ? { headerCell: () => null } : {},
          cell: ({ dataItem, ...props }) => (
            <CheckboxCell
              checked={!!selected?.find((s) => s[identifierColumn] === dataItem[identifierColumn])}
              {...props}
              onChange={onItemSelectHandler(dataItem)}
            />
          ),
        },
        ...result,
      ];
    }
    return result;
  };

  if (status === requestStatuses.INIT || status === requestStatuses.PENDING || isLoading) {
    return <GridLoader />;
  }

  if (status === requestStatuses.FAILED) {
    return <GridErrorMessage name={name} error={error} />;
  }

  const innerColumns = buildColumns();

  // Used to set inner components id and data-testid
  const getGridContentId = (contentId) => (`${id ? `${id}-` : ''}${contentId}`);

  const hotKeyMap = {
    RESET_SORT: 'alt+a',
  };

  const handleHotKeyResetSort = () => {
    resetSort?.();
  };

  const hotKeyHandlers = {
    RESET_SORT: handleHotKeyResetSort,
  };

  return (
    <BaseHotKeys
      keyMap={hotKeyMap}
      handlers={hotKeyHandlers}
    >
      {requestExport ? (
        <CustomExcelExport ref={exporterRef} data={exported} fileName={fileName} columns={exportColumns} />
      ) : (
        <ExcelExport ref={_export} fileName={fileName} />
      )}
      <GridComponent
        id={id}
        ref={_grid}
        hideActionsCell={hideActionsCell || recycleBin || readOnly}
        readOnly={readOnly}
        name={name}
        data={passedData ?? items}
        columns={innerColumns}
        groupable={groupable}
        group={group}
        sortable={innerSortable}
        sort={sort}
        onSortChange={onSortChange}
        skip={page.skip}
        pageSize={page.take}
        total={total}
        subTotal={subTotal}
        identifierColumn={identifierColumn}
        onPageChange={(event) => {
          customPageChangeHandler?.(event);
          onPageChange(event);
        }}
        hideActionBar={hideActionBar}
        selectedField='checked'
        selectable={
          selectionEnabled || onRowSelect
            ? {
              enabled: true,
              drag: false,
              cell: false,
              mode: onRowSelect ? 'single' : 'multiple',
            }
            : false
        }
        onRowSelect={onRowSelectHandler}
        onHeaderSelectionChange={onHeaderSelectionChange}
        hideAddButton={onAdd || disableAdd || recycleBin || readOnly || initialRecycleBin}
        actionBarContent={
          !hideActionBar && (
            <>
              {additionalButtons && additionalButtons}
              {(!readOnly || forceAdd)
                && usePageAdd
                && onAdd
                && !disableAdd
                && !recycleBin
                && !initialRecycleBin
                && !props.hideAddButton && <AddButton id={getGridContentId('add-button')} onClick={onAdd} />}
              {hasExport && (
                <ExportButton
                  id={getGridContentId('export-button')}
                  isLoading={exportStatus === requestStatuses.PENDING}
                  onClick={onExport}
                />
              )}
              {hasRecycleBin && !readOnly && (
                <RecycleBinButton
                  name={name}
                  id={getGridContentId('recycle-bin-button')}
                  value={recycleBin}
                  onShow={() => {
                    showRecycleBin();
                    if (onShowRecycleBin) {
                      onShowRecycleBin();
                    }
                  }}
                  onHide={() => {
                    hideRecycleBin();
                    if (onHideRecycleBin) {
                      onHideRecycleBin();
                    }
                  }}
                />
              )}
              {filteringOptions && (
                <>
                  <div>
                    {filterApplied && (
                      <span className='k-icon notifications'>
                        <Badge shape='dot' themeColor='error' />
                      </span>
                    )}
                    <FilterButton
                      id={getGridContentId('filter-button')}
                      onClick={() => setShowFilter(true)}
                    />
                  </div>
                  {filterApplied && (
                    <Button
                      id={getGridContentId('clear-filter-button')}
                      title='Clear Filter'
                      onClick={handleClearFilter}
                    />
                  )}
                </>
              )}
              {enableColumnsToDisplay && (
                <Button
                  id={getGridContentId('columns-to-show-button')}
                  icon='eye'
                  title='Columns To Show'
                  onClick={() => setColumnsToDisplayOpened(true)}
                />
              )}
              {enableBulkChange && !bulkChangeActive && !readOnly && !recycleBin && (
                <Button
                  id={getGridContentId('change-all-button')}
                  icon='edit'
                  title='Change All'
                  onClick={() => handleBulkChangeClick(true)}
                />
              )}
              {enableBulkChange && bulkChangeActive && !readOnly && !recycleBin && (
                <Button icon='check' title='Set Fields' onClick={() => handleBulkChangeClick(false)} />
              )}
              {actionBarContent}
            </>
          )
        }
        didChange={reloadOnBackground}
        searchParams={searchParams}
        restored={restored}
        resultCount={passedData ? (props.total || (passedData || []).length) : total}
        showResultCount={showResultCount}
        isBgLoading={isBgLoading}
        {...props}
      />
      {filteringOptions && (
        <Modal
          isOpen={showFilter}
          handleClose={() => {
            setShowFilter(false);
          }}
          containerStyle='large'
          title='Filtering'
        >
          <FilteringPanel
            entity={filteringOptions.entity}
            onApply={handleFilterApplied}
            initialFilter={currentFilter}
          />
        </Modal>
      )}
      <ColumnsToDisplayModal
        isOpen={columnsToDisplayOpened}
        close={() => setColumnsToDisplayOpened(false)}
        columnsToDisplay={columnsToDisplay}
        {...props}
      />
    </BaseHotKeys>
  );
};

/**
 * DataGrid component.
 *
 * @typedef {object} DataGridProps
 * @property {string} title - Title for the data grid.
 * @property {string} subtitle - Subtitle for the data grid.
 * @property {boolean} autoSearch - Automatically trigger search (default: true).
 * @property {object} initialSearchParams - Initial search parameters.
 * @property {object} resetSearchParams - Search params values to be used on filters reset
 * @property {function} formatSearchParams - Function to format search parameters.
 * @property {function} formatParamsForQuery - Function to format search parameters for search query string
 * @property {function} formatSearchQuery - Function to format URL search query for initial search params
 * @property {boolean} allowResetFilters - Allow resetting filters (default: true).
 * @property {object} searchParams - Parent search parameters.
 * @property {boolean} hideFiltersBar - Hide the filters bar (default: false).
 * @property {boolean} hasUnifiedTextSearch - Enable unified text search (default: true).
 * @property {string} initialTextSearch - Initial text search value.
 * @property {string} unifiedTextSearchHint - Hint for unified text search.
 * @property {Array} filterFields - Fields for filtering.
 * @property {function} onReset - Callback for resetting filters.
 * @property {function} onSearchChanged - Callback for search changes.
 * @property {boolean} hasRecycleBin - Enable recycle bin (default: false).
 * @property {boolean} initialRecycleBin - Initial recycle bin state (default: false).
 * @property {boolean} useSearchQuery - Use search query.
 * @property {Array} additionalButtons - Additional buttons.
 * @property {number} resultCount - Total result count.
 * @property {boolean} showResultCount - Show result count.
 * @property {boolean} useFilterCache - Use filter cache.
 * @property {ReactNode} filtersExtraStart - Extra content at the start of filters.
 * @property {ReactNode} filtersExtraEnd - Extra content at the end of filters.
 * @property {...import('../BaseGrid/BaseGrid').BaseGridProps} props - Additional props.
 *
 * @param {DataGridProps} allProps - Component props.
 * @returns {React.ReactElement} - DataGrid component.
 */
const DataGrid = (allProps) => {
  const {
    title,
    subtitle,
    // search params and filters
    autoSearch = true,
    initialSearchParams,
    resetSearchParams,
    formatSearchParams,
    formatParamsForQuery,
    formatSearchQuery,
    allowResetFilters = true,
    searchParams: parentSearchParams,
    hideFiltersBar = false,
    hasUnifiedTextSearch = true,
    initialTextSearch,
    unifiedTextSearchHint,
    filterFields,
    onReset,
    onSearchChanged,
    // recycle bin
    hasRecycleBin = false,
    initialRecycleBin = false,
    useSearchQuery,
    additionalButtons,
    resultCount,
    showResultCount,
    useFilterCache,
    filtersExtraStart,
    filtersExtraEnd,
    searchDebounceTimer = 500,
  } = allProps;

  const defaultSearchParams = useMemo(() => {
    const hasSearch = hasUnifiedTextSearch && initialTextSearch?.length > 0;
    const initValue = { ...(initialSearchParams ?? {}), deletedOnly: hasRecycleBin ? initialRecycleBin : undefined };
    return hasSearch ? { ...initValue, textSearch: initialTextSearch } : initValue;
  }, [hasUnifiedTextSearch, initialTextSearch, initialSearchParams, hasRecycleBin, initialRecycleBin]);

  const { getCache, clearGridCache } = useDataGridSettingsCache(useFilterCache);

  const gridCache = useMemo(() => {
    const cache = getCache?.();
    if (cache && location.pathname === cache?.pathname) {
      clearGridCache();
      return cache?.data;
    }
  }, []);

  const {
    setSearchParams, reset, recycleBin, showRecycleBin, hideRecycleBin, formatted,
  } = useSearchParams(
    useFilterCache && gridCache ? gridCache : defaultSearchParams,
    formatSearchParams,
    {
      useSearchQuery, parentSearchParams, formatDates: true, formatSearchQuery, formatParamsForQuery, resetSearchParams,
    },
  );

  // filters
  const innerFilterFields = hasUnifiedTextSearch
    ? [
      {
        name: 'search',
        label: 'Search',
        hint: unifiedTextSearchHint,
        Component: Input,
        className: 'items-search-input',
        icon: 'search',
        debounceTimer: searchDebounceTimer,
        autoFocus: true,
      },
      ...(filterFields ?? []),
    ]
    : filterFields;

  const handleReset = () => {
    reset();

    if (onReset) {
      onReset();
    }
  };

  useEffect(() => {
    onSearchChanged?.(formatted);
  }, [formatted]);

  return (
    <div className='data-grid-container'>
      {!hideFiltersBar && (
        <GridFiltersBar
          title={title}
          subtitle={subtitle}
          autoSearch={autoSearch}
          searchParams={formatted}
          onSearch={setSearchParams}
          onReset={handleReset}
          filterFields={innerFilterFields}
          defaultSearchParams={resetSearchParams ?? defaultSearchParams}
          allowReset={allowResetFilters}
          fieldsExtraStart={filtersExtraStart}
          fieldsExtraEnd={filtersExtraEnd}
        />
      )}
      <GridContent
        recycleBin={recycleBin}
        showRecycleBin={showRecycleBin}
        hideRecycleBin={hideRecycleBin}
        searchParams={formatted}
        hideFiltersBar={hideFiltersBar}
        additionalButtons={additionalButtons}
        resultCount={resultCount}
        showResultCount={showResultCount}
        {...allProps}
      />
    </div>
  );
};

export default DataGrid;
